'use client';
import {
  Tabs as MaterialTabs,
  TabsHeader as MaterialTabsHeader,
  Tab as MaterialTab,
} from '@material-tailwind/react';
import classNames from 'classnames';
import React from 'react';
import { MaterialIconName } from '../../../types/materialIconName';
import Whatsapp from '../../icons/Whatsapp';
import { MaterialIcon } from '../../MaterialIcon';
import Tooltip from '../../Tooltip';

interface TabsCheckboxProps {
  label?: string;
  options: { label: string; value: string }[];
  value?: string;
  onChange: (value: string) => void;
  labelClass?: string;
  tabsHeaderClass?: string;
  tabClass?: string;
  indicatorClass?: string;
  iconClass?: string;
  activeIconColor?: string;
  inactiveIconColor?: string;
  whatsappIconSize?: number;
  tooltip?: string;
}

export const TabsCheckbox: React.FC<TabsCheckboxProps> = ({
  onChange,
  options,
  value,
  label,
  tabsHeaderClass = '',
  tabClass = '',
  indicatorClass = '',
  labelClass = '',
  tooltip = '',
}) => {
  return (
    <div className="relative z-1 flex flex-col gap-1">
      {!!label?.length && (
        <div className="flex items-center gap-1">
          <p
            className={`font-medium text-gray-800 text-base leading-4 ${labelClass}`}
          >
            {label}
          </p>
          {!!tooltip && (
            <Tooltip
              content={
                <span className="text-white alpha-caption">{tooltip}</span>
              }
              placement="top"
            ></Tooltip>
          )}
        </div>
      )}
      <MaterialTabs value={value ?? ''}>
        <MaterialTabsHeader
          className={`h-9 rounded-lg p-0.5 !bg-gray-200 ${tabsHeaderClass}`}
          indicatorProps={{ className: `rounded-lg ${indicatorClass}` }}
        >
          {options.map((option) => (
            <MaterialTab
              key={option.value}
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className={classNames(
                `w-full font-semibold text-gray-600 text-base sm:px-2 sm:py-[6px] md:px-4 md:py-[7px] ${tabClass}`,
                { '!text-gray-900': option.value === value },
              )}
              value={option.value}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </MaterialTab>
          ))}
        </MaterialTabsHeader>
      </MaterialTabs>
    </div>
  );
};

export const TabsIconsCheckbox: React.FC<TabsCheckboxProps> = ({
  onChange,
  options,
  value,
  label,
  labelClass,
  tabsHeaderClass,
  iconClass,
  indicatorClass = '',
}) => {
  return (
    <div className="flex w-full flex-col gap-2">
      {!!label && (
        <p
          className={`font-medium text-gray-800 text-base leading-4 ${labelClass}`}
        >
          {label}
        </p>
      )}
      <MaterialTabs value={value ?? ''}>
        <MaterialTabsHeader
          className={`h-9 rounded-lg p-0.5 !bg-gray-200 ${tabsHeaderClass}`}
          indicatorProps={{
            id: 'tabs-header-indicator',
            className: `rounded-lg ${indicatorClass}`,
          }}
        >
          {options.map((option) => (
            <MaterialTab
              key={option.value}
              className="flex items-center justify-center [&_#tabs-header-indicator]:!translate-x-0"
              value={option.value}
              onClick={() => onChange(option.value)}
              // The tab's only visible content is an icon, and that icon is
              // aria-hidden (MaterialIcon hides itself when no aria-label is
              // passed, and the Whatsapp SVG has no built-in label either),
              // so without this the tab has no accessible name at all. Tab
              // forwards unknown props onto the underlying <li>.
              aria-label={option.label}
            >
              {option.value === 'WA' && (
                <Whatsapp
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className={classNames('size-[22px]', {
                    'text-buttons/neutral': option.value !== value,
                    'text-buttons/neutral-dark': option.value === value,
                  })}
                />
              )}
              {option.value !== 'WA' && (
                <MaterialIcon
                  icon={option.label as MaterialIconName}
                  className={classNames(
                    `mt-1 text-gray-900 text-2xl ${iconClass}`,
                    { '!text-gray-600': option.value !== value },
                  )}
                />
              )}
            </MaterialTab>
          ))}
        </MaterialTabsHeader>
      </MaterialTabs>
    </div>
  );
};
